import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";
import { Loader2, UserPlus, UserMinus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface FollowButtonProps {
    targetId: string;
    type: "user" | "shop";
    className?: string;
    variant?: "default" | "outline" | "secondary" | "ghost";
    size?: "default" | "sm" | "lg" | "icon";
}

export function FollowButton({ targetId, type, className, variant = "outline", size = "sm" }: FollowButtonProps) {
    const { t } = useI18n();
    const { toast } = useToast();

    // Query follow status
    const { data: status, isLoading: isLoadingStatus } = useQuery<{ following: boolean }>({
        queryKey: [`/api/social/follow-status/${type}/${targetId}`],
        staleTime: 60000,
    });

    const isFollowing = status?.following;

    // Mutation for follow/unfollow
    const mutation = useMutation({
        mutationFn: async () => {
            const endpoint = isFollowing
                ? `/api/social/unfollow/${type}/${targetId}`
                : `/api/social/follow/${type}/${targetId}`;

            const res = await apiRequest("POST", endpoint);
            return res.json();
        },
        onMutate: async () => {
            // Optimistic update
            await queryClient.cancelQueries({ queryKey: [`/api/social/follow-status/${type}/${targetId}`] });
            const previousStatus = queryClient.getQueryData([`/api/social/follow-status/${type}/${targetId}`]);

            queryClient.setQueryData([`/api/social/follow-status/${type}/${targetId}`], { following: !isFollowing });

            return { previousStatus };
        },
        onError: (err, variables, context) => {
            if (context?.previousStatus) {
                queryClient.setQueryData([`/api/social/follow-status/${type}/${targetId}`], context.previousStatus);
            }
            toast({
                variant: "destructive",
                title: t("common.error"),
                description: t("rating.genericError"),
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [`/api/social/follow-status/${type}/${targetId}`] });
            queryClient.invalidateQueries({ queryKey: [`/api/social/counts/${type}/${targetId}`] });
        },
    });

    const handleToggle = () => {
        mutation.mutate();
    };

    const isCircle = size === "icon" || className?.includes("rounded-full");

    const toggleButton = (
        <Button
            variant={isFollowing ? "outline" : "default"}
            size={size}
            onClick={() => !isFollowing && handleToggle()}
            disabled={mutation.isPending}
            className={cn(
                "transition-all duration-200",
                isCircle ? "rounded-full w-10 h-10 p-0 flex items-center justify-center shadow-lg" : "min-w-[100px]",
                !isFollowing && "bg-magenta hover:bg-magenta/90 text-white border-none shadow-magenta/20",
                isFollowing && "border-magenta/20 text-magenta hover:bg-magenta/5",
                className
            )}
            title={isFollowing ? t("social.unfollow") : t("social.follow")}
        >
            {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : isFollowing ? (
                <UserMinus className={isCircle ? "h-5 w-5" : "h-4 w-4 mr-2"} />
            ) : (
                <UserPlus className={isCircle ? "h-5 w-5" : "h-4 w-4 mr-2"} />
            )}

            {!isCircle && (isFollowing ? t("social.unfollow") : t("social.follow"))}
        </Button>
    );

    if (isLoadingStatus && !status) {
        return (
            <Button disabled variant={variant} size={size} className={cn("min-w-[100px]", className)}>
                <Loader2 className="h-4 w-4 animate-spin" />
            </Button>
        );
    }

    if (isFollowing) {
        return (
            <AlertDialog>
                <AlertDialogTrigger asChild>
                    {toggleButton}
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("social.unfollowConfirmTitle") || "Ontvolgen?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("social.unfollowConfirmDesc") || "Weet je zeker dat je wilt stoppen met volgen?"}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleToggle} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            {t("social.unfollow") || "Ontvolgen"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        );
    }

    return toggleButton;
}
